<template>
  <div>
    <slot></slot>
    <draggable v-model="optionElements" v-bind="{animation: 150}" class="matrix-input">
      <property-option
        v-for="(checkbox, i) in optionElements"
        :key="checkbox.client_id"
        :data-id="checkbox.client_id"
        :model="checkbox"
        :is-focused="isFocused(i)"
        v-on:blur="handleBlur(checkbox)"
        v-on:keydown:enter="handleEnter(checkbox)"
        v-on:remove="deleteChildProperty"
        v-on:paste:bulk="handlePasteBulk"
      ></property-option>
    </draggable>
    <div class="cta">
      <input
        type="button"
        value="Add new value"
        @click="add"
        class="btn grey-to-blue xs"
      >
    </div>

    <dynamic-child-paste-modal
      v-if="modalStateFor(pasteModalId, ModalStatus.Confirmation).isVisible"
      :state="modalStateFor(pasteModalId)"
      @toggle-modal="toggleModal"
      @paste="singlePaste"
      @bulk-add="bulkAdd"></dynamic-child-paste-modal>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import propertyBaseMixin from "../mixins/property-base-mixin";
  import PropertyOption from "./property-option.vue";
  import Draggable from 'vuedraggable';
  import pasteable from "../../../common/mixins/pasteable";
  import {DelimiterType} from "../../../common/helpers/delimiterType";
  import DynamicChildPasteModal from "../../../common/components/dynamicChildPasteModal.vue";
  import {ObjectModelPropertyChildType} from "../../../common/enums/objectModelPropertyType";
  import {NumberHelper} from "../../../common/helpers/numberHelper";
  import ObjectModelProperty from "../../../common/models/objectModelProperties/objectModelProperty";

  export default Vue.extend({
    components: {
      PropertyOption,
      Draggable,
      DynamicChildPasteModal,
    },
    mixins: [
      propertyBaseMixin,
      pasteable
    ],
    data: function () {
      return {
        defaultLabelTitle: "New item",
        focusIndex: null,
        pasteData: {},
      };
    },
    computed: {
      optionElements: {
        get(): any[] {
          return this.objectModelProperty.objectModelPropertiesArray.sort((a, b) => a.sort_order - b.sort_order);
        },
        set(elements: any[]) {
          for (let i = 0, len = elements.length; i < len; i++) {
            elements[i].sort_order = i;
          }
        }
      },
      pasteModalId(): string {
        return `pasteModalId_${this.objectModelProperty.client_id}`;
      },
      itemCount(): number {
        return this.optionElements.length;
      }
    },
    methods: {
      addValue(position: number = null): ObjectModelProperty {
        if (NumberHelper.toIntOrNull(position) === null) {
          position = this.optionElements.length;
        }
        return this.objectModelProperty.objectModelPropertyHandler.append(ObjectModelPropertyChildType.PickListOption, position);
      },
      handlePasteBulk(data): void {
        this.pasteData = data;
        if (data.type === DelimiterType.newLine) {
          this.bulkAdd();
        } else {
          this.showModal(this.pasteModalId);
        }
      },
      bulkAdd(): void {
        this.paste(this.pasteData.options[0], this.pasteData.event.target, this.pasteData.model, 'label_title');
        this.pasteData.options
            .reduce((acc, current, index) => {
              if (index === 0) {
                // already handled first
                return acc;
              }
              const trimmed = current.trim();
              if (trimmed.length > 0) {
                acc.push(trimmed);
              }
              return acc;
            }, [])
            .forEach((text, i) => {
              this.addValue(this.pasteData.index + 1 + i).label_title = text;
            });
        this.resetPasteModal();
      },
      singlePaste() {
        this.paste(this.pasteData.text, this.pasteData.event.target, this.pasteData.model, 'label_title');
        this.resetPasteModal();
      },
      resetPasteModal(): void {
        this.hideModal(this.pasteModalId);
        this.pasteData = {};
        this.modalStateFor(this.pasteModalId).status = this.ModalStatus.Confirmation;
      },
      deleteChildProperty(optionElement: any): void {
        this.$emit('remove', optionElement);
      },
      add(): void {
        this.addValue();
        this.$nextTick(() => {
          this.focusIndex = this.itemCount - 1;
        });
      },
      isFocused(i) {
        return i === this.focusIndex;
      },
      handleBlur(optionElement: any): void {
        // don't delete is bulk paste modal is visible
        if (!this.modalStateFor(this.pasteModalId).isVisible) {
          this.focusIndex = null;
          if (!(optionElement.hasLabelTitle)) {
            optionElement.destroy();
          }
        }
      },
      handleEnter(optionElement: any): void {
        let newIndex = optionElement.sort_order;
        if (!(optionElement.hasLabelTitle || optionElement.isLast)) {
          // if element's label_title is empty and is not the last element
          optionElement.destroy();
        } else {
          if (optionElement.isLast) {
            this.addValue();
          }
          newIndex += 1;
        }
        this.$nextTick(() => this.focusIndex = newIndex);
      }
    }
  });
</script>
